import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  MapPin,
  Moon,
  Sun,
  Maximize,
  Minimize,
} from "feather-icons-react/build/IconComponents";

/* ==========================================================================
   Shared chrome for the transaction screens that wear the Sales Invoice
   terminal design: command bar, status bar, column chips, and the view
   preferences behind them (theme, full screen, "More" row, visible columns).
   Nothing here touches form data - it only changes what is drawn.
   ========================================================================== */

// the Sales Invoice uses this same key, so the chosen theme follows the
// operator from screen to screen
const THEME_KEY = "salesInvoice.theme";

const readPref = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
  } catch {
    return fallback;
  }
};

const writePref = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable - the choice just will not persist */
  }
};

/**
 * View preferences for one screen.
 * @param {string} screenKey   prefix for this screen's saved preferences
 * @param {object} defaultCols optional grid columns, { key: visibleByDefault }
 */
export const useTerminalChrome = (screenKey, defaultCols = {}) => {
  const [theme, setTheme] = useState(
    () => readPref(THEME_KEY, { mode: "light" }).mode
  );
  const toggleTheme = () =>
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      writePref(THEME_KEY, { mode: next });
      return next;
    });

  const [isFullScreen, setIsFullScreen] = useState(
    () => typeof document !== "undefined" && !!document.fullscreenElement
  );
  useEffect(() => {
    const sync = () => setIsFullScreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);
  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      const req = document.documentElement.requestFullscreen?.();
      if (req?.catch)
        req.catch(() => toast.error("Full screen was blocked - press F11"));
    } else {
      document.exitFullscreen?.();
    }
  };

  const stripKey = `${screenKey}.strip`;
  const [moreOpen, setMoreOpen] = useState(
    () => readPref(stripKey, { more: false }).more
  );
  const toggleMore = () =>
    setMoreOpen((prev) => {
      writePref(stripKey, { more: !prev });
      return !prev;
    });

  const colsKey = `${screenKey}.columns`;
  const [cols, setCols] = useState(() => readPref(colsKey, defaultCols));
  const toggleCol = (key) =>
    setCols((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      writePref(colsKey, next);
      return next;
    });
  // a switched-off column stays mounted (the print builders read the cells),
  // it is just not drawn
  const colClass = (key) => (cols[key] === false ? "si-col-off" : "");
  // table min-width follows the columns actually showing
  const tableWidth = (base, widths) =>
    Object.keys(widths).reduce(
      (w, key) => (cols[key] === false ? w - widths[key] : w),
      base
    );

  return {
    theme,
    toggleTheme,
    isFullScreen,
    toggleFullScreen,
    moreOpen,
    toggleMore,
    cols,
    toggleCol,
    colClass,
    tableWidth,
  };
};

/** Command bar: back, document title / number / state, operator cluster. */
export const TermCmdBar = ({ chrome, title, docNo, state, onBack }) => {
  const { userName, userId } = useSelector((s) => s.userauth);
  const { UserListByBranch } = useSelector((s) => s.userservice || {});
  const { branch } = useSelector((s) => s.branchlist);

  const listedName = (UserListByBranch || []).find(
    (u) => u.guid === userId?.id
  )?.name;
  const operator =
    (typeof userName === "string" && userName) || listedName || "";
  const initials = `${operator}`
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

  return (
    <header className="si-cmd">
      <button
        type="button"
        className="si-back"
        onClick={onBack}
        title="Back to the list"
        aria-label="Back"
      >
        <ArrowLeft />
      </button>
      <div className="si-doc">
        <h1>{title}</h1>
        {docNo !== undefined && (
          <span className="si-doc-no">{docNo || "—"}</span>
        )}
        {state && (
          <span className={`si-state si-state--${state.key}`}>
            {state.label}
          </span>
        )}
      </div>
      <div className="si-spacer" />
      <div className="si-cmd-actions">
        {operator && (
          <div className="si-userchip" title="Signed in">
            <span className="si-av">{initials}</span>
            <span className="si-un">{operator}</span>
          </div>
        )}
        {operator && branch?.name && <span className="si-sepbar" />}
        {branch?.name && (
          <span className="si-branch" title="Branch">
            <MapPin />
            {branch.name}
          </span>
        )}
        <button
          type="button"
          className="si-iconbtn"
          onClick={chrome.toggleTheme}
          title={chrome.theme === "dark" ? "Switch to light" : "Switch to dark"}
          aria-label="Switch light or dark theme"
        >
          {chrome.theme === "dark" ? <Sun /> : <Moon />}
        </button>
        <button
          type="button"
          className="si-iconbtn"
          onClick={chrome.toggleFullScreen}
          title={chrome.isFullScreen ? "Exit full screen" : "Full screen"}
          aria-label="Full screen"
        >
          {chrome.isFullScreen ? <Minimize /> : <Maximize />}
        </button>
      </div>
    </header>
  );
};

/** "More" toggle closing the first header row. */
export const TermMoreToggle = ({ chrome }) => (
  <button
    type="button"
    className="si-moretoggle"
    aria-expanded={chrome.moreOpen}
    onClick={chrome.toggleMore}
  >
    <span>More</span>
    <svg
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden="true"
    >
      <path d="M3 5l3 3 3-3" />
    </svg>
  </button>
);

/** Column chips bar above the grid; children render on the right. */
export const TermColumns = ({ chrome, columns, children }) => (
  <div className="si-cols">
    {columns.length > 0 && <span className="si-lbl">Columns</span>}
    {columns.map((col) => (
      <button
        key={col.key}
        type="button"
        className="si-colchip"
        aria-pressed={chrome.cols[col.key] !== false}
        onClick={() => chrome.toggleCol(col.key)}
      >
        {col.label}
      </button>
    ))}
    <div className="si-cols-right">{children}</div>
  </div>
);

// keyboard hints for screens with the scan bar (see ItemEntry.jsx)
export const ENTRY_HINTS = [
  [["F2"], "Party"],
  [["F3"], "Item search"],
  [["F9"], "Save & print"],
  [["Enter"], "Next line"],
  [["Ctrl", "Del"], "Remove line"],
];

const BASIC_HINTS = [
  [["Tab"], "Next field"],
  [["Shift", "Tab"], "Previous field"],
  [["Esc"], "Close list"],
];

/** Bottom status bar: keyboard hints on the left, context on the right. */
export const TermStatusBar = ({ context = [], hints = BASIC_HINTS, children }) => (
  <footer className="si-status">
    {children}
    {children && <span className="si-sepbar" />}
    {hints.map(([keys, label]) => (
      <span className="si-grp" key={label}>
        {keys.map((k) => (
          <span className="si-kbd" key={k}>
            {k}
          </span>
        ))}
        {label}
      </span>
    ))}
    <span className="si-sp" />

    <span className="si-grp si-grp--ctx">
      {context.filter(Boolean).join(" · ")}
    </span>
  </footer>
);
